/**
 * Canonical label registry for user-facing field names and statuses.
 *
 * No snake_case, no machine strings, no code-shaped text reaches the UI.
 * All rendering of field paths, statuses, and file types goes through here.
 */

const FIELD_LABELS: Record<string, string> = {
  tenant_name: "Tenant",
  suite_id: "Suite",
  leased_sf: "Leased SF",
  lease_start_date: "Lease Start",
  lease_expiration_date: "Lease Expiration",
  base_rent_annual_psf: "Base Rent ($/SF)",
  lease_type: "Lease Type",
  rent_escalation_type: "Escalation Type",
  rent_escalation_value: "Escalation Value",
  pro_rata_share: "Pro-Rata Share",
  rent_steps: "Rent Steps",
  cam_type: "CAM Type",
  base_year: "Base Year",
  base_year_amount: "Base Year Amount",
  expense_cap_pct: "Expense Cap %",
  fixed_cam_psf: "Fixed CAM ($/SF)",
  cam_exclusions: "CAM Exclusions",
  total_leasable_area: "Total Leasable Area",
  gross_building_area: "Gross Building Area",
  year: "Year",
  total_revenue: "Total Revenue",
  total_expenses: "Total Expenses",
  real_estate_taxes: "Real Estate Taxes",
  insurance: "Insurance",
  utilities: "Utilities",
  repairs_and_maintenance: "Repairs & Maintenance",
  management_fee: "Management Fee",
  janitorial: "Janitorial",
  landscaping: "Landscaping",
  administrative: "Administrative",
  billed_amount: "Billed Amount",
  paid_amount: "Paid Amount",
  expense_pool_assignments: "Expense Pool Assignments",
  name: "Name",
  address: "Address",
  space_status: "Space Status",
  credit_rating: "Credit Rating",
  security_deposit: "Security Deposit",
  // CAM structure and reimbursement fields
  expense_cap_compounding: "Expense Cap Compounding",
  true_up_adjustment: "True-Up Adjustment",
  stop_amount: "Stop Amount",
  stop_amount_psf: "Stop Amount ($/SF)",
  tax_reimbursement: "Tax Reimbursement",
  insurance_reimbursement: "Insurance Reimbursement",
  controllable_only: "Controllable Only",
  // Rent step subfields
  effective_date: "Effective Date",
  rent_psf: "Rent ($/SF)",
  // Operating expense categories
  capital_expenditures: "Capital Expenditures",
  reserves: "Reserves",
  security: "Security",
  noi: "Net Operating Income",
  base_rent: "Base Rent",
  recovery_income: "Recovery Income",
  other_income: "Other Income",
  // Additional parse/document fields (previously local to DocumentsPage)
  base_rent_annual: "Annual Rent",
  lease_dates: "Lease Dates",
  total_sf: "Total Square Footage",
  total_annual_rent: "Total Annual Rent",
  occupancy: "Occupancy",
  tenant_count: "Tenant Count",
};

const STATUS_LABELS: Record<string, string> = {
  verified: "Verified",
  corrected: "Corrected",
  unverified: "Needs Review",
  flag: "Flagged",
  pending: "Pending",
  complete: "Complete",
  error: "Error",
  parsing: "Parsing",
  enhancing: "Enhancing",
  // Deal statuses
  draft: "Draft",
  active: "Active",
  under_loi: "Under LOI",
  under_contract: "Under Contract",
  closed: "Closed",
  dead: "Dead",
  archived: "Archived",
};

const FILE_TYPE_LABELS: Record<string, string> = {
  rent_roll: "Rent Roll",
  operating_statement: "Operating Statement",
  cam_rec: "CAM Reconciliation",
};

/**
 * Canonical labels for enum values emitted by the backend (engine/models.py).
 * Anything displayed as a field VALUE (not a field name) routes through here.
 */
const VALUE_LABELS: Record<string, string> = {
  // LeaseType / CamType / reimbursement types
  NNN: "NNN",
  NN: "NN",
  gross: "Gross",
  modified_gross: "Modified Gross",
  base_year_stop: "Base Year Stop",
  expense_cap: "Expense Cap",
  excluded: "Excluded",
  fixed_psf: "Fixed PSF",
  FSG: "FSG",
  pending: "Pending",
  none: "None",
  // SpaceStatus
  leased: "Leased",
  speculative: "Speculative",
  dark_tenant: "Dark Tenant",
  vacant: "Vacant",
  mtm: "Month-to-Month",
  // RentEscalationType
  fixed_percent: "Fixed Percent",
  fixed_amount: "Fixed Amount",
  fixed_dollar: "Fixed Dollar",
  step_rent: "Step Rent",
  CPI: "CPI",
  cpi: "CPI",
  flat: "Flat",
  // RentEscalationFrequency
  annual: "Annual",
  other: "Other",
  // AssetClass
  office: "Office",
  retail: "Retail",
  industrial: "Industrial",
  mixed_use: "Mixed-Use",
  // CamExclusion
  roof: "Roof",
  structural: "Structural",
  management: "Management",
  capital: "Capital",
  anchor_specific: "Anchor Specific",
  // CamPoolType
  full: "Full",
  controllable_only: "Controllable Only",
  custom: "Custom",
  // Booleans (sometimes arrive as strings from JSON)
  true: "Yes",
  false: "No",
  True: "Yes",
  False: "No",
};

// Snake_case identifier: one or more lowercase letter groups separated by
// underscores. Excludes values with digits, spaces, hyphens, or mixed case —
// those are tenant names, dates, dollar amounts, etc., and should pass through.
const SNAKE_CASE_IDENT = /^[a-z]+(_[a-z]+)+$/;

/**
 * Render any parsed value for display. Enum strings become friendly labels;
 * numbers are formatted with locale separators; names/dates/amounts pass
 * through unchanged.
 */
export function prettyValue(val: unknown): string {
  if (val === null || val === undefined) return "--";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "number") {
    if (Number.isInteger(val) && Math.abs(val) >= 1000) {
      return val.toLocaleString("en-US");
    }
    return val.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  if (typeof val !== "string") return String(val);

  if (VALUE_LABELS[val]) return VALUE_LABELS[val];

  if (SNAKE_CASE_IDENT.test(val)) {
    return val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return val;
}

/**
 * Convert a field path like "tenants[3].base_rent_annual_psf" into a
 * human-readable label like "Base Rent ($/SF)".
 */
export function prettyFieldLabel(fieldPath: string): string {
  // Extract the last segment
  const parts = fieldPath.split(".");
  const last = parts[parts.length - 1] || fieldPath;
  // Strip array indices
  const key = last.replace(/\[\d+\]/g, "");

  if (FIELD_LABELS[key]) return FIELD_LABELS[key];

  // Fallback: title-case with underscores replaced, pct → %, psf → /SF
  return key
    .replace(/_/g, " ")
    .replace(/\bpct\b/g, "%")
    .replace(/\bpsf\b/g, "/SF")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build a section label like "Tenant: Starbucks - Suite 101" from a field's context.
 */
export function prettySectionLabel(
  section: string,
  _fieldPath?: string,
): string {
  // Already formatted sections pass through
  if (!section.includes("_")) return section;
  return section.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Convert a status code to a friendly label.
 */
export function prettyStatus(status: string): string {
  return STATUS_LABELS[status] || status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Convert a file_type code to a friendly label.
 */
export function prettyFileType(fileType: string): string {
  return FILE_TYPE_LABELS[fileType] || fileType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
